import { format } from 'date-fns';
import { type ReactNode, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type CustomField, CustomFieldTypeName } from '@air/api/types';
import { parseAirDateToISO } from '@air/utilities';

import { CustomFieldListWithOverflow } from '~/components/CustomFields/CustomFieldListWithOverflow';
import Linkify from '~/components/Linkify';
import { TABLE_CELL_H_PADDING } from '~/components/TableView/ui';
import { TABLE_CUSTOM_FIELD_CELL_BUTTON } from '~/constants/testIDs';
import { visibleCustomFieldsViewOptionsSelector } from '~/store/configViews/selectors';
import { type AssignedCustomFieldValue } from '~/swr-hooks/customFields/types';
import { EditableTableCell } from '../../EditableTableCell/EditableTableCell';
import { useTableView } from '../../TableViewContext';
import { makeActiveTableColumnFromOption } from '../../tableColumnWidthsUtils';
import { getTableColumnWidth } from '../../types';
import { getAdditionalPopperOffset } from './utils';
export type BaseCustomFieldTableCellProps = {
  customField?: CustomField;
  customFieldName: string;
  customFieldId: string;
  assignedCustomFieldValue?: AssignedCustomFieldValue;
  renderEditableCustomField?: (closeEditor: () => void) => ReactNode;
};

export const BaseCustomFieldTableCell = ({
  customField,
  customFieldName,
  customFieldId,
  assignedCustomFieldValue,
  renderEditableCustomField,
}: BaseCustomFieldTableCellProps) => {
  const { getColumnWidthStyle } = useTableView();
  const visibleCustomFields = useSelector(visibleCustomFieldsViewOptionsSelector);
  const customFields = useMemo(
    () =>
      assignedCustomFieldValue
        ? [assignedCustomFieldValue].filter(({ id }) => visibleCustomFields.find((vcf) => vcf.customFieldId === id))
        : [],
    [assignedCustomFieldValue, visibleCustomFields],
  );

  const shouldRenderValue = useMemo(() => {
    return (
      !!assignedCustomFieldValue?.dateValue ||
      !!assignedCustomFieldValue?.plainTextValue ||
      !!assignedCustomFieldValue?.value ||
      !!assignedCustomFieldValue?.values?.length
    );
  }, [assignedCustomFieldValue]);

  const renderValue = useCallback(
    (openEditor: () => void) => {
      if (!assignedCustomFieldValue) return null;
      switch (assignedCustomFieldValue.type.title) {
        case CustomFieldTypeName.singleSelect:
        case CustomFieldTypeName.multiSelect: {
          return (
            <CustomFieldListWithOverflow
              className="p-1"
              onClick={openEditor}
              customFields={customFields}
              allowedWidth={getTableColumnWidth('customField') - TABLE_CELL_H_PADDING * 2}
              data-testid={`${TABLE_CUSTOM_FIELD_CELL_BUTTON}-${customFieldName}`}
            />
          );
        }
        case CustomFieldTypeName.plainText: {
          return (
            <button
              className="h-5 w-full truncate text-left"
              data-testid="TABLE_CELL_CUSTOM_FIELD_PLAIN_TEXT"
              onClick={openEditor}
              type="button"
            >
              <Linkify>{assignedCustomFieldValue.plainTextValue || ''}</Linkify>
            </button>
          );
        }
        case CustomFieldTypeName.date: {
          const dateText = assignedCustomFieldValue.dateValue
            ? format(parseAirDateToISO(assignedCustomFieldValue.dateValue), 'MMM dd, yyyy')
            : '';
          return (
            <button className="h-5 w-full text-left" onClick={openEditor} type="button">
              {dateText}
            </button>
          );
        }
      }
    },
    [customFields, customFieldName, assignedCustomFieldValue],
  );

  const additionalPopperOffset = useMemo(
    () => getAdditionalPopperOffset(customField, assignedCustomFieldValue),
    [customField, assignedCustomFieldValue],
  );

  const tableCellStyle = useMemo(
    () => getColumnWidthStyle(makeActiveTableColumnFromOption(customFieldName, true, customFieldId)),
    [getColumnWidthStyle, customFieldName, customFieldId],
  );

  return (
    <EditableTableCell
      columnWidth={getTableColumnWidth('customField')}
      data-testid={`${TABLE_CUSTOM_FIELD_CELL_BUTTON}-${customFieldName}`}
      renderValue={shouldRenderValue && renderValue}
      renderEditor={renderEditableCustomField}
      additionalPopperOffset={additionalPopperOffset}
      tableCellStyle={tableCellStyle}
    />
  );
};
